import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { vorgangService, type VorgangDto } from "../../services/vorgangService";
import { kundeName } from "../../utils/vorgangScope";
import { REFINED } from "../risikoanalyseStep/tokens";
import { I } from "../redesign/kvpIcons";

/**
 * Papierkorb der Vorgänge. Seit Vorgänge soft-gelöscht werden (statt hart, mit
 * Cascade auf To-dos und Notizen), braucht es einen Weg zurück — den gab es für
 * Vorgänge und To-dos bisher gar nicht: `todo.service.restore()` existierte, hing
 * aber an keinem Endpoint, und die `PapierkorbPage` kennt nur Kontakte.
 *
 * Bewusst schlank gehalten (eigene Datei statt weiterer Block in VorgaengeMo):
 * Liste + Wiederherstellen, keine Filter, keine Pagination. Der Endpoint liefert
 * ohnehin nur, was der Aufrufer auch wiederherstellen dürfte, und deckelt auf 200.
 */

const deDateTime = (iso?: string | Date | null): string => {
    if (!iso) return "—";
    const d = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

type Props = {
    open: boolean;
    onClose: () => void;
    /** Wird nach erfolgreicher Wiederherstellung gerufen (Liste neu laden). */
    onRestored: () => void;
};

export const PapierkorbDialog: React.FC<Props> = ({ open, onClose, onRestored }) => {
    const [rows, setRows] = useState<VorgangDto[] | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const laden = useCallback(() => {
        setFehler(null);
        setRows(null);
        vorgangService
            .getDeletedVorgaenge()
            .then(setRows)
            .catch(() => {
                setRows([]);
                setFehler("Der Papierkorb konnte nicht geladen werden.");
            });
    }, []);

    useEffect(() => {
        if (open) laden();
    }, [open, laden]);

    const wiederherstellen = async (id: number) => {
        setBusyId(id);
        try {
            await vorgangService.restoreVorgang(id);
            setRows((prev) => (prev ?? []).filter((v) => v.id !== id));
            onRestored();
        } catch {
            setFehler("Wiederherstellen fehlgeschlagen.");
        } finally {
            setBusyId(null);
        }
    };

    if (!open) return null;

    return (
        <div
            className="kvp-rd"
            role="dialog"
            aria-modal="true"
            aria-label="Papierkorb"
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 10000,
                backgroundColor: "rgba(24,18,28,0.42)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            }}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                style={{
                    backgroundColor: REFINED.white,
                    borderRadius: 14,
                    border: `1px solid ${REFINED.line}`,
                    boxShadow: "0 18px 48px rgba(24,18,28,0.28)",
                    width: "min(720px, 100%)",
                    maxHeight: "min(80vh, 720px)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        padding: "16px 20px",
                        borderBottom: `1px solid ${REFINED.line}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                    }}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: REFINED.fontUI, fontWeight: 700, fontSize: "1.02rem", color: REFINED.ink }}>
                            Papierkorb
                        </div>
                        <div style={{ fontSize: "0.84rem", color: REFINED.ink3 }}>
                            Gelöschte Vorgänge — Wiederherstellen holt auch die mitgelöschten To-dos zurück.
                        </div>
                    </div>
                    <button type="button" className="tbar-btn" onClick={onClose}>
                        Schließen
                    </button>
                </div>

                <div style={{ overflowY: "auto", padding: "8px 12px 16px" }}>
                    {fehler && (
                        <div style={{ padding: "10px 8px", color: REFINED.err, fontSize: "0.88rem" }}>{fehler}</div>
                    )}
                    {rows == null && !fehler && (
                        <div style={{ padding: "18px 8px", color: REFINED.ink3, fontSize: "0.9rem" }}>Lade …</div>
                    )}
                    {rows != null && rows.length === 0 && !fehler && (
                        <div style={{ padding: "18px 8px", color: REFINED.ink3, fontSize: "0.9rem" }}>
                            Der Papierkorb ist leer.
                        </div>
                    )}
                    {(rows ?? []).map((v) => (
                        <div
                            key={v.id}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                padding: "10px 8px",
                                borderBottom: `1px solid ${REFINED.line}`,
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontFamily: REFINED.fontUI,
                                        fontWeight: 600,
                                        fontSize: "0.94rem",
                                        color: REFINED.ink,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {v.titel}
                                </div>
                                <div style={{ fontSize: "0.82rem", color: REFINED.ink2 }}>
                                    {kundeName(v.kunde)} · gelöscht {deDateTime(v.deleted)}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="tbar-btn"
                                disabled={busyId === v.id}
                                onClick={() => void wiederherstellen(v.id)}
                                style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                            >
                                {I.recycle}
                                {busyId === v.id ? "…" : "Wiederherstellen"}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PapierkorbDialog;
